import React from 'react';
import { VaultRefsLine } from './VaultRefsLine';
import ExperienceBlock from './ExperienceBlock';
import EducationBlock from './EducationBlock';
import ProjectBlock from './ProjectBlock';
import InternshipBlock from './InternshipBlock';
import SkillsBlock from './SkillsBlock';
import HeaderBlock from './HeaderBlock';

export const SummaryBlock = ({ items }) => {
  const text = typeof items === 'string' ? items : (items?.text || items?.summary || '');
  return (
    <p 
      style={{
        fontSize: 'var(--font-size-body, 9pt)',
        color: 'var(--color-text-main, #2C3E50)',
        lineHeight: 'var(--line-height-body, 1.45)',
        margin: 0,
        textAlign: 'justify',
        maxWidth: '100%',
      }}
    >
      {text}
    </p>
  );
};

/**
 * InlineListBlock — compact dot-separated single-line list.
 * Handles: flat strings, { language, proficiency }, { name, level }, { name } objects.
 * Used by both languages and any section configured with style: 'inline'.
 */
export const InlineListBlock = ({ items = [] }) => {
  if (!items || items.length === 0) return null;

  const formatted = items.map(item => {
    if (typeof item === 'string') return item;
    const name = item.name || item.language || item.skill || item.title || '';
    const level = typeof item.proficiency === 'string' ? item.proficiency
      : typeof item.level === 'string' ? item.level : '';
    return name + (level ? ` (${level})` : '');
  }).filter(Boolean);

  if (formatted.length === 0) return null;

  return (
    <div
      style={{
        fontSize: 'var(--font-size-body, 9pt)',
        color: 'var(--color-text-main)',
        lineHeight: 'var(--line-height-body, 1.45)',
        fontWeight: 500,
        letterSpacing: '0.01em',
      }}
    >
      {formatted.join('  •  ')}
    </div>
  );
};

// Keep LanguagesBlock as an alias for backwards-compat
export const LanguagesBlock = InlineListBlock;

export const BulletListBlock = ({ items = [] }) => {
  if (!items || items.length === 0) return null;
  return (
    <ul style={{ margin: '0', paddingLeft: '12px', listStyleType: 'var(--bullet-style, disc)' }}>
      {items.map((item, i) => {
        const text = typeof item === 'string' ? item : (item.name || item.title || item.description || JSON.stringify(item));
        return (
          <li 
            key={i} 
            style={{ 
              fontSize: 'var(--font-size-bullet, 8.8pt)', 
              lineHeight: 'var(--line-height-bullet, 1.35)', 
              color: 'var(--color-text-main)', 
              marginBottom: 'var(--spacing-bullet-gap, 4px)' 
            }}
          >
            {text}
          </li>
        );
      })}
    </ul>
  );
};

export const GenericDetailBlock = ({ items = [] }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map((item, idx) => {
        if (typeof item === 'string') {
          return (
            <div key={idx} style={{ fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-text-main)' }}>
              • {item}
            </div>
          );
        }
        
        const title = item.title || item.name || item.role || item.language || item.network || '';
        const subtitle = item.issuer || item.platform || item.organization || item.publisher || item.office || item.company || item.event || item.username || '';
        const date = item.date || item.year || item.duration || item.proficiency || '';
        const url = item.url || '';
        const description = item.description || item.content || item.contact_info || '';
        const points = item.points || [];

        return (
          <div key={idx} style={{ marginBottom: '2px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-text-main)' }}>
                {title} {subtitle && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>| {subtitle}</span>}
              </div>
              {date && (
                <div style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                  {date}
                </div>
              )}
            </div>
            {url && (
              <div style={{ fontSize: '8pt', color: 'var(--brand)', textDecoration: 'none', margin: '2px 0' }}>
                {url}
              </div>
            )}
            {description && (
              <p style={{ fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-text-main)', margin: '2px 0 0', lineHeight: 1.4 }}>
                {description}
              </p>
            )}
            {Array.isArray(points) && points.length > 0 && (
              <ul style={{ margin: '4px 0 0', paddingLeft: '16px', listStyleType: 'circle' }}>
                {points.map((pt, pIdx) => pt && (
                  <li key={pIdx} style={{ fontSize: 'var(--font-size-bullet, 8.5pt)', color: 'var(--color-text-main)', marginBottom: '2px' }}>
                    {pt}
                  </li>
                ))}
              </ul>
            )}
            <VaultRefsLine refs={item.vault_refs} />
          </div>
        );
      })}
    </div>
  );
};

export const CustomSectionBlock = ({ items }) => {
  if (!items) return null;
  const content = items.content || '';
  return (
    <div>
      {content && (
        <p style={{ fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-text-main)', margin: 0, whiteSpace: 'pre-line', lineHeight: 1.4 }}>
          {content}
        </p>
      )}
    </div>
  );
};

export const sectionRegistry = {
  // Headers
  'header-classic': (props) => <HeaderBlock {...props} layout="classic" />,
  'header-centered': (props) => <HeaderBlock {...props} layout="centered" />,
  'header-split': (props) => <HeaderBlock {...props} layout="split" />,
  'header-executive': (props) => <HeaderBlock {...props} layout="executive" />,
  'header-creative': (props) => <HeaderBlock {...props} layout="creative" />,
  'header-hero': (props) => <HeaderBlock {...props} layout="hero" />,
  
  // Summary
  'summary-section': SummaryBlock,
  
  // Experience
  'experience-classic': (props) => <ExperienceBlock {...props} variant="classic" />,
  'experience-timeline': (props) => <ExperienceBlock {...props} variant="timeline" />,
  'experience-compact': (props) => <ExperienceBlock {...props} variant="compact" />,
  'experience-impact': (props) => <ExperienceBlock {...props} variant="impact" />,
  'experience-metrics': (props) => <ExperienceBlock {...props} variant="metrics" />,
  'experience-executive': (props) => <ExperienceBlock {...props} variant="executive" />,
  
  // Projects
  'projects-list': (props) => <ProjectBlock {...props} variant="list" />,
  'projects-cards': (props) => <ProjectBlock {...props} variant="cards" />,
  'projects-case-study': (props) => <ProjectBlock {...props} variant="case-study" />,
  'projects-timeline': (props) => <ProjectBlock {...props} variant="timeline" />,
  'projects-feature-block': (props) => <ProjectBlock {...props} variant="feature-block" />,
  
  // Skills
  'skills-simple-list': (props) => <SkillsBlock {...props} variant="skills-simple-list" />,
  'skills-tags': (props) => <SkillsBlock {...props} variant="skills-tags" />,
  'skills-categorized': (props) => <SkillsBlock {...props} variant="skills-categorized" />,
  'skills-sidebar': (props) => <SkillsBlock {...props} variant="skills-sidebar" />,
  'skills-badge-grid': (props) => <SkillsBlock {...props} variant="skills-badge-grid" />,
  
  // Education
  'education-classic': (props) => <EducationBlock {...props} variant="classic" />,
  'education-timeline': (props) => <EducationBlock {...props} variant="timeline" />,
  'education-compact': (props) => <EducationBlock {...props} variant="compact" />,
  'education-card': (props) => <EducationBlock {...props} variant="card" />,
  'education-sidebar': (props) => <EducationBlock {...props} variant="sidebar" />,
  
  // Secondary / Dynamic Registry blocks
  'languages-section': InlineListBlock,
  'inline-list': InlineListBlock,
  'education-section': (props) => <EducationBlock {...props} variant="classic" />,
  'certifications-section': BulletListBlock,
  'achievements-section': BulletListBlock,
  'extracurricular-section': BulletListBlock,
  'internships-section': InternshipBlock,


  // Modular Component Resolutions
  'ContactRenderer': (props) => null, // contact rendered inline in header
  'SummaryRenderer': SummaryBlock,
  'ExperienceRenderer': (props) => <ExperienceBlock {...props} variant="classic" />,
  'ProjectRenderer': (props) => <ProjectBlock {...props} variant="list" />,
  'SkillsRenderer': (props) => <SkillsBlock {...props} variant="skills-categorized" />,
  'EducationRenderer': (props) => <EducationBlock {...props} variant="classic" />,
  'GenericListRenderer': GenericDetailBlock,
  'TagListRenderer': InlineListBlock,
  'CustomSectionRenderer': CustomSectionBlock,

  // Canvas Custom Split Blocks
  'header-canvas-main': ({ personalInfo = {} }) => {
    const name = personalInfo.name || 'Your Name';
    const roleTitle = personalInfo.roleTitle || personalInfo.role || '';
    return (
      <div style={{ marginBottom: 'var(--spacing-section-mb, 16px)', width: '100%' }}>
        <h1 
          style={{ 
            fontSize: 'var(--font-size-display, 26pt)', 
            fontWeight: 'var(--weight-display, 800)',
            color: 'var(--color-primary, #4F46E5)', 
            margin: '0 0 6px 0',
            textTransform: 'uppercase',
            letterSpacing: 'var(--letter-spacing-display, 0.5px)',
            lineHeight: 'var(--line-height-display, 1.2)'
          }}
        >
          {name}
        </h1>
        {roleTitle && (
          <div 
            style={{ 
              fontSize: 'var(--font-size-subheading, 9.5pt)', 
              fontWeight: 'var(--weight-subheading, 700)', 
              color: 'var(--color-secondary, #6366F1)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              margin: '0 0 16px 0'
            }}
          >
            {roleTitle}
          </div>
        )}
      </div>
    );
  },

  'contact-sidebar': ({ personalInfo = {} }) => {
    const email = personalInfo.email || '';
    const phone = personalInfo.phone || '';
    const location = personalInfo.location || personalInfo.city || '';
    const links = personalInfo.links || {};
    const linkedin = links.linkedin || personalInfo.linkedin || '';
    const github = links.github || personalInfo.github || '';
    const portfolio = links.portfolio || personalInfo.portfolio || personalInfo.website || '';

    const stripUrl = (url) => {
      if (!url) return '';
      return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    };

    const items = [
      location ? { icon: '📍', text: location } : null,
      email ? { icon: '✉', text: email, isLink: true, href: `mailto:${email}` } : null,
      phone ? { icon: '☎', text: phone } : null,
      linkedin ? { icon: '🔗', text: 'LinkedIn', isLink: true, href: linkedin.includes('http') ? linkedin : `https://${linkedin}` } : null,
      github ? { icon: '💻', text: 'GitHub', isLink: true, href: github.includes('http') ? github : `https://github.com/${github}` } : null,
      portfolio ? { icon: '🔗', text: stripUrl(portfolio), isLink: true, href: portfolio.includes('http') ? portfolio : `https://${portfolio}` } : null,
    ].filter(Boolean);

    return (
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px', 
          fontSize: 'var(--font-size-meta, 8pt)', 
          color: 'var(--color-text-main, #FFFFFF)', 
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1.5px solid var(--color-border)'
        }}
      >
        {items.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
            <span style={{ fontSize: '10pt', flexShrink: 0 }}>{item.icon}</span>
            {item.isLink ? (
              <a 
                href={item.href} 
                target="_blank" 
                rel="noreferrer" 
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  opacity: 0.95,
                  wordBreak: 'break-all',
                  fontWeight: 500
                }}
              >
                {item.text}
              </a>
            ) : (
              <span style={{ opacity: 0.95, wordBreak: 'break-all', fontWeight: 500 }}>{item.text}</span>
            )}
          </div>
        ))}
      </div>
    );
  }
};

export function getSectionComponent(id) {
  return sectionRegistry[id] || GenericDetailBlock;
}

export default sectionRegistry;
